import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { 
  Card, CardHeader, CardTitle, CardContent,
  Button, Badge,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty
} from '../components/ui';
import { formatCurrency, formatNumber } from '../lib/utils';
import { ChevronLeft, ChevronRight, ArrowLeft, TrendingUp, Eye, ExternalLink } from 'lucide-react';

const getMonthLabel = (date) => date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const getMonthRange = (year, month) => ({
  start: new Date(year, month, 1).toISOString().split('T')[0],
  end: new Date(year, month + 1, 0).toISOString().split('T')[0]
});

export default function AdSenseUrls() {
  const { user } = useAuth();
  const [urlData, setUrlData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthOffset, setMonthOffset] = useState(0);

  const getViewMonth = useCallback(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth() - monthOffset, 1);
  }, [monthOffset]);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    
    try {
      const vm = getViewMonth();
      const { start, end } = getMonthRange(vm.getFullYear(), vm.getMonth());
      
      const { data } = await supabase
        .from('adsense_urls')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', start)
        .lte('date', end);

      // Aggregate by URL
      const urlMap = {};
      (data || []).forEach(row => {
        if (!urlMap[row.url]) {
          urlMap[row.url] = { 
            url: row.url, 
            net_revenue: 0, 
            gross_revenue: 0,
            page_views: 0, 
            impressions: 0,
            clicks: 0, 
            days: 0 
          };
        }
        urlMap[row.url].net_revenue += Number(row.net_revenue) || 0;
        urlMap[row.url].gross_revenue += Number(row.gross_revenue) || 0;
        urlMap[row.url].page_views += Number(row.page_views) || 0;
        urlMap[row.url].impressions += Number(row.impressions) || 0;
        urlMap[row.url].clicks += Number(row.clicks) || 0;
        urlMap[row.url].days++;
      });

      // Calculate RPM and CTR for each URL
      const aggregated = Object.values(urlMap).map(u => ({
        ...u,
        rpm: u.page_views > 0 ? (u.net_revenue / u.page_views) * 1000 : 0,
        ctr: u.impressions > 0 ? (u.clicks / u.impressions) * 100 : 0,
        cpc: u.clicks > 0 ? u.net_revenue / u.clicks : 0,
      }));

      // Sort by revenue descending
      aggregated.sort((a, b) => b.net_revenue - a.net_revenue);
      setUrlData(aggregated);
    } catch (e) {
      console.error(e);
    }
    
    setLoading(false);
  }, [user, getViewMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const monthLabel = getMonthLabel(getViewMonth());
  const totalRevenue = urlData.reduce((s, u) => s + u.net_revenue, 0);
  const totalPV = urlData.reduce((s, u) => s + u.page_views, 0);
  const totalImpressions = urlData.reduce((s, u) => s + u.impressions, 0);
  const totalClicks = urlData.reduce((s, u) => s + u.clicks, 0);

  // Top performers
  const topByRevenue = [...urlData].slice(0, 5);
  const topByRpm = [...urlData].sort((a, b) => b.rpm - a.rpm).slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/adsense">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">AdSense URL Stats</h1>
            <p className="text-muted-foreground">Revenue by domain/URL</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{monthLabel} Revenue</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Page Views</p>
            <p className="text-xl font-bold">{formatNumber(totalPV)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Clicks</p>
            <p className="text-xl font-bold">{formatNumber(totalClicks)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Unique Domains</p>
            <p className="text-xl font-bold">{urlData.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              Top by Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topByRevenue.length === 0 ? (
              <p className="text-muted-foreground text-sm">No data</p>
            ) : (
              <div className="space-y-2">
                {topByRevenue.map((u, i) => (
                  <div key={u.url} className="flex justify-between items-center text-sm">
                    <span className="truncate max-w-[60%]" title={u.url}>
                      <Badge variant="outline" className="mr-2">{i + 1}</Badge>
                      <a href={u.url.startsWith('http') ? u.url : `https://${u.url}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary hover:underline">
                        {u.url}
                      </a>
                    </span>
                    <span className="font-medium text-green-600">{formatCurrency(u.net_revenue)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="h-4 w-4 text-blue-500" />
              Top by RPM
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topByRpm.length === 0 ? (
              <p className="text-muted-foreground text-sm">No data</p>
            ) : (
              <div className="space-y-2">
                {topByRpm.map((u, i) => (
                  <div key={u.url} className="flex justify-between items-center text-sm">
                    <span className="truncate max-w-[60%]" title={u.url}>
                      <Badge variant="outline" className="mr-2">{i + 1}</Badge>
                      <a href={u.url.startsWith('http') ? u.url : `https://${u.url}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary hover:underline">
                        {u.url}
                      </a>
                    </span>
                    <span className="font-medium text-blue-600">${u.rpm.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* URL Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle>All Domains</CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => setMonthOffset(Math.min(2, monthOffset + 1))} 
                disabled={monthOffset >= 2}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium min-w-[130px] text-center">{monthLabel}</span>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => setMonthOffset(Math.max(0, monthOffset - 1))} 
                disabled={monthOffset <= 0}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Domain</TableHead>
                    <TableHead className="text-right">Page Views</TableHead>
                    <TableHead className="text-right">Impr</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">CTR</TableHead>
                    <TableHead className="text-right">CPC</TableHead>
                    <TableHead className="text-right">RPM</TableHead>
                    <TableHead className="text-right">Net Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {urlData.length === 0 ? (
                    <TableEmpty colSpan={8} message={`No URL data for ${monthLabel}`} />
                  ) : (
                    urlData.map(u => (
                      <TableRow key={u.url}>
                        <TableCell className="font-medium max-w-[250px]">
                          <a 
                            href={u.url.startsWith('http') ? u.url : `https://${u.url}`} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center gap-1 hover:text-primary group"
                            title={u.url}
                          >
                            <span className="truncate">{u.url}</span>
                            <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                          </a>
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(u.page_views)}</TableCell>
                        <TableCell className="text-right">{formatNumber(u.impressions)}</TableCell>
                        <TableCell className="text-right">{formatNumber(u.clicks)}</TableCell>
                        <TableCell className="text-right">{u.ctr.toFixed(2)}%</TableCell>
                        <TableCell className="text-right">${u.cpc.toFixed(2)}</TableCell>
                        <TableCell className="text-right">${u.rpm.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {formatCurrency(u.net_revenue)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}